import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type NodeExecStatus = 'idle' | 'starting' | 'streaming' | 'completed' | 'error' | 'awaiting_human';

export interface HITLNodeConfig {
  availableActions: string[];
  assignedRole: string;
  backupRole: string;
  slaHours: number;
  businessCriticality: string;
}

export interface NodeExecutionState {
  status: NodeExecStatus;
  streamingText: string;
  output: string | null;
  rawOutputs: any;
  aiworkerUsageId: string | null;
  error: string | null;
  startedAt: string | null;
  completedAt: string | null;
  // HITL suspension fields
  pendingRecordId: string | null;
  capturedContext: any;
  hitlNodeConfig: HITLNodeConfig | null;
}

export interface WorkflowExecutionState {
  isRunning: boolean;
  isSuspended: boolean;
  suspendedAtNodeId: string | null;
  isPanelOpen: boolean;
  nodeStates: Record<string, NodeExecutionState>;
  error: string | null;
  runId: string | null;
}

const defaultNodeState = (): NodeExecutionState => ({
  status: 'idle',
  streamingText: '',
  output: null,
  rawOutputs: null,
  aiworkerUsageId: null,
  error: null,
  startedAt: null,
  completedAt: null,
  pendingRecordId: null,
  capturedContext: null,
  hitlNodeConfig: null,
});

const initialState: WorkflowExecutionState = {
  isRunning: false,
  isSuspended: false,
  suspendedAtNodeId: null,
  isPanelOpen: false,
  nodeStates: {},
  error: null,
  runId: null,
};

export const workflowExecutionSlice = createSlice({
  name: 'workflowExecution',
  initialState,
  reducers: {
    startExecution: (state, action: PayloadAction<{ nodeIds: string[]; runId: string }>) => {
      state.isRunning = true;
      state.isPanelOpen = true;
      state.error = null;
      state.runId = action.payload.runId;
      state.nodeStates = {};
      action.payload.nodeIds.forEach((id) => {
        state.nodeStates[id] = defaultNodeState();
      });
    },
    setNodeStarting: (state, action: PayloadAction<string>) => {
      const ns = state.nodeStates[action.payload];
      if (ns) {
        ns.status = 'starting';
        ns.startedAt = new Date().toISOString();
        ns.streamingText = '';
        ns.output = null;
        ns.error = null;
      }
    },
    setNodeStreaming: (state, action: PayloadAction<string>) => {
      const ns = state.nodeStates[action.payload];
      if (ns) ns.status = 'streaming';
    },
    appendNodeChunk: (state, action: PayloadAction<{ nodeId: string; text: string }>) => {
      const ns = state.nodeStates[action.payload.nodeId];
      if (ns) {
        ns.status = 'streaming';
        ns.streamingText += action.payload.text;
      }
    },
    completeNode: (
      state,
      action: PayloadAction<{
        nodeId: string;
        output: string;
        rawOutputs: any;
        aiworkerUsageId: string | null;
      }>
    ) => {
      const ns = state.nodeStates[action.payload.nodeId];
      if (ns) {
        ns.status = 'completed';
        ns.output = action.payload.output;
        ns.rawOutputs = action.payload.rawOutputs;
        ns.aiworkerUsageId = action.payload.aiworkerUsageId;
        ns.completedAt = new Date().toISOString();
      }
    },
    suspendNode: (
      state,
      action: PayloadAction<{
        nodeId: string;
        pending: {
          pendingRecordId: string;
          capturedContext: any;
          nodeConfig: HITLNodeConfig;
          nodeLabel: string;
        };
      }>
    ) => {
      const ns = state.nodeStates[action.payload.nodeId];
      if (ns) {
        ns.status = 'awaiting_human';
        ns.output = 'Awaiting human review';
        ns.completedAt = new Date().toISOString();
        ns.pendingRecordId = action.payload.pending.pendingRecordId;
        ns.capturedContext = action.payload.pending.capturedContext;
        ns.hitlNodeConfig = action.payload.pending.nodeConfig;
      }
      state.isSuspended = true;
      state.suspendedAtNodeId = action.payload.nodeId;
      state.isRunning = false;
    },
    failNode: (state, action: PayloadAction<{ nodeId: string; error: string }>) => {
      const ns = state.nodeStates[action.payload.nodeId];
      if (ns) {
        ns.status = 'error';
        ns.error = action.payload.error;
        ns.completedAt = new Date().toISOString();
      }
    },
    completeExecution: (state) => {
      state.isRunning = false;
    },
    failExecution: (state, action: PayloadAction<string>) => {
      state.isRunning = false;
      state.error = action.payload;
    },
    openExecutionPanel: (state) => {
      state.isPanelOpen = true;
    },
    closeExecutionPanel: (state) => {
      state.isPanelOpen = false;
    },
    resetExecution: (state) => {
      state.isRunning = false;
      state.isSuspended = false;
      state.suspendedAtNodeId = null;
      state.isPanelOpen = false;
      state.nodeStates = {};
      state.error = null;
      state.runId = null;
    },
    resumeExecution: (state) => {
      state.isSuspended = false;
      state.suspendedAtNodeId = null;
      state.isRunning = true;
      state.error = null;
    },
  },
});

export const {
  startExecution,
  setNodeStarting,
  setNodeStreaming,
  appendNodeChunk,
  completeNode,
  suspendNode,
  failNode,
  completeExecution,
  failExecution,
  openExecutionPanel,
  closeExecutionPanel,
  resetExecution,
  resumeExecution,
} = workflowExecutionSlice.actions;

export default workflowExecutionSlice.reducer;
